import React, { useState, useEffect } from 'react';
import { Table, Card, Row, Col, Button, Modal, Descriptions, Form, Input, Select, Space, DatePicker, Tag, Dropdown, message } from 'antd';
import { EyeOutlined, SearchOutlined, MoreOutlined } from '@ant-design/icons';
import useRequest from '../hooks/useRequest';
import apiPath from '../constants/apiPath';
import { ShowToast } from '../helper/toast';

const { Option } = Select;
const { RangePicker } = DatePicker;

const VendorPayouts = () => {
  const { request } = useRequest();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [statusChangeModalVisible, setStatusChangeModalVisible] = useState(false);
  const [payoutToUpdate, setPayoutToUpdate] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusForm] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentCycleFilter, setPaymentCycleFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // NEW: Reason modal and state
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [reasonForm] = Form.useForm();
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { payout, status }

  const statusColors = {
    'Pending': 'orange',
    'Under Review': 'blue',
    'Approved': 'green',
    'Paid': 'green',
    'Rejected': 'red'
  };

  const paymentCycleColors = {
    'Weekly': 'blue',
    'Bi-weekly': 'purple',
    'Monthly': 'cyan'
  };

  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Paid', label: 'Paid' },
    { value: 'Under Review', label: 'Under Review' },
    { value: 'Rejected', label: 'Rejected' }
  ];

  const columns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 150,
    },
    {
      title: 'Vendor Name',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 150,
    },
    {
      title: 'Service Name',
      dataIndex: 'service_name',
      key: 'service_name',
      width: 150,
    },
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      width: 180,
    },
    {
      title: 'Service Date',
      dataIndex: 'date_of_service',
      key: 'date_of_service',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
      width: 120,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => amount ? `$${amount}` : '-',
      width: 100,
      align: 'right',
    },
    {
      title: 'Payment Cycle',
      dataIndex: 'vendor_payment_cycle',
      key: 'vendor_payment_cycle',
      render: (cycle) => cycle ? (
        <Tag color={paymentCycleColors[cycle] || 'default'}>{cycle}</Tag>
      ) : '-',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>
          {status}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleString() : '-',
      width: 180,
      sorter: true,
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewPayout(record)}
          >
            View
          </Button>
          <Dropdown
            menu={{
              items: statusOptions
                .filter(option => option.value !== record.status)
                .map(option => ({
                  key: option.value,
                  label: (
                    <span onClick={() => handleStatusChangeClick(record, option.value)}>
                      Change to {option.label}
                    </span>
                  ),
                })),
            }}
            placement="bottomRight"
          >
            <Button size="small" icon={<MoreOutlined />}>
              Status
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  const fetchPayouts = async (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);
    
    const params = new URLSearchParams({
      page,
      page_size: pageSize,
    });

    if (filters.searchKeyword) {
      params.append('search', filters.searchKeyword);
    }
    if (filters.status && filters.status !== "all") {
      params.append('status', filters.status);
    }
    if (filters.paymentCycle && filters.paymentCycle !== "all") {
      params.append('payment_cycle', filters.paymentCycle);
    }
    if (filters.dateRange && filters.dateRange.length === 2) {
      params.append('start_date', filters.dateRange[0].format('YYYY-MM-DD'));
      params.append('end_date', filters.dateRange[1].format('YYYY-MM-DD'));
    }

    await request({
      url: `${apiPath.getVendorPayouts}?${params}`,
      method: 'GET',
      onSuccess: (response) => {
        if (response.status) {
          setPayouts(response.data);
          setPagination({
            current: page,
            pageSize,
            total: response.pagination.total_count,
          });
        }
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    fetchPayouts();
  }, []);

  const handleStatusChangeClick = (payout, status) => {
    const needsReason = ['Under Review', 'Rejected'].includes(status);
    if (needsReason) {
      setPendingStatusChange({ payout, status });
      reasonForm.resetFields();
      setReasonModalVisible(true);
    } else {
      setPayoutToUpdate(payout);
      setNewStatus(status);
      setStatusChangeModalVisible(true);
    }
  };

  const handleStatusChange = async () => {
    if (!payoutToUpdate || !newStatus) return;

    const reason = reasonForm.getFieldValue('reason'); // Get reason if entered

    await request({
      url: `${apiPath.updateVendorPayoutStatus}`,
      method: 'PATCH',
      data: {
        id: payoutToUpdate.id,
        status: newStatus,
        ...(reason ? { reason } : {}), // Send reason only if present
      },
      onSuccess: (response) => {
        if (response.status) {
          message.success(`Status updated to ${newStatus} successfully`);
          setStatusChangeModalVisible(false);
          setPayoutToUpdate(null);
          setNewStatus('');
          reasonForm.resetFields();
          setPendingStatusChange(null);
          fetchPayouts(pagination.current, pagination.pageSize, {
            searchKeyword,
            status: statusFilter,
            paymentCycle: paymentCycleFilter,
            dateRange
          });
        } else {
          message.error(response.message || 'Failed to update status');
        }
      },
      onError: (error) => {
        message.error(error.message || 'Failed to update status');
      }
    });
  };

  const handleTableChange = (paginationInfo, filters, sorter) => {
    fetchPayouts(
      paginationInfo.current, 
      paginationInfo.pageSize, 
      { 
        searchKeyword, 
        status: statusFilter, 
        paymentCycle: paymentCycleFilter,
        dateRange 
      }
    );
  };

  const handleSearch = (values) => {
    const { keyword } = values;
    setSearchKeyword(keyword);
    fetchPayouts(1, pagination.pageSize, { 
      searchKeyword: keyword, 
      status: statusFilter, 
      paymentCycle: paymentCycleFilter,
      dateRange 
    });
  };

  const handleAutoSearch = (keyword) => {
    setSearchKeyword(keyword);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      fetchPayouts(1, pagination.pageSize, { 
        searchKeyword: keyword, 
        status: statusFilter, 
        paymentCycle: paymentCycleFilter,
        dateRange 
      });
    }, 500);
    
    setSearchTimeout(newTimeout);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchPayouts(1, pagination.pageSize, { 
      searchKeyword, 
      status: value, 
      paymentCycle: paymentCycleFilter,
      dateRange 
    });
  };

  const handlePaymentCycleFilterChange = (value) => {
    setPaymentCycleFilter(value);
    fetchPayouts(1, pagination.pageSize, { 
      searchKeyword, 
      status: statusFilter, 
      paymentCycle: value,
      dateRange 
    });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    fetchPayouts(1, pagination.pageSize, { 
      searchKeyword, 
      status: statusFilter, 
      paymentCycle: paymentCycleFilter,
      dateRange: dates 
    });
  };

  const resetFilters = () => {
    setSearchKeyword("");
    setStatusFilter("all");
    setPaymentCycleFilter("all");
    setDateRange(null);
    searchForm.resetFields();
    fetchPayouts(1, pagination.pageSize);
  };

  const handleViewPayout = (payout) => {
    setSelectedPayout(payout);
    setViewModalVisible(true);
  };

  return (
    <div className="vendor-payouts">
      <Card title="Vendor Pay-outs" style={{ margin: '20px' }}>
        {/* Search and Filter Section */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="vertical"
            onFinish={handleSearch}
            initialValues={{ 
              keyword: "", 
              status: "all", 
              paymentCycle: "all",
              dateRange: null 
            }}
          >
            <Row gutter={[16, 16]} align="bottom">
              {/* Search Input */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Search Payouts" name="keyword">
                  <Input
                    placeholder="Search by invoice, order ID..."
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => handleAutoSearch(e.target.value)}
                  />
                </Form.Item>
              </Col>

              {/* Status Filter */}
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Status" name="status">
                  <Select onChange={handleStatusFilterChange}>
                    <Option value="all">All Status</Option>
                    <Option value="Pending">Pending</Option>
                    <Option value="Approved">Approved</Option>
                    <Option value="Paid">Paid</Option>
                    <Option value="Under Review">Under Review</Option>
                    <Option value="Rejected">Rejected</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Payment Cycle Filter */}
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Payment Cycle" name="paymentCycle">
                  <Select onChange={handlePaymentCycleFilterChange}>
                    <Option value="all">All Cycles</Option>
                    <Option value="Weekly">Weekly</Option>
                    <Option value="Bi-weekly">Bi-weekly</Option>
                    <Option value="Monthly">Monthly</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Date Range Filter */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Date Range" name="dateRange">
                  <RangePicker 
                    style={{ width: '100%' }}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>

              {/* Action Buttons */}
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label=" " colon={false}>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                      Search
                    </Button>
                    <Button onClick={resetFilters}>Reset</Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {/* Active Filters Display */}
          {(searchKeyword || statusFilter !== "all" || paymentCycleFilter !== "all" || dateRange) && (
            <div style={{ marginTop: 16, padding: 8, background: "#f0f8ff", borderRadius: 6 }}>
              <Space wrap>
                <strong>Active Filters:</strong>
                {searchKeyword && (
                  <span style={{ background: "#e6f7ff", padding: "2px 8px", borderRadius: 4 }}>
                    Search: "{searchKeyword}"
                  </span>
                )}
                {statusFilter !== "all" && (
                  <span style={{ background: "#e6f7ff", padding: "2px 8px", borderRadius: 4 }}>
                    Status: {statusFilter}
                  </span>
                )}
                {paymentCycleFilter !== "all" && (
                  <span style={{ background: "#e6f7ff", padding: "2px 8px", borderRadius: 4 }}>
                    Payment Cycle: {paymentCycleFilter}
                  </span>
                )}
                {dateRange && (
                  <span style={{ background: "#e6f7ff", padding: "2px 8px", borderRadius: 4 }}>
                    Date: {dateRange[0].format('MMM DD, YYYY')} - {dateRange[1].format('MMM DD, YYYY')}
                  </span>
                )}
                <Button type="link" size="small" onClick={resetFilters}>
                  Clear All
                </Button>
              </Space>
            </div>
          )}
        </Card>

        <Table
          columns={columns}
          dataSource={payouts}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      {/* View Payout Modal */}
      <Modal
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        className='tab_modal'
        width={700}
      >
        <div className='modal_header'>
          Payout Details
        </div>
        {selectedPayout && (
          <Descriptions bordered column={1} size="middle">
            <Descriptions.Item label="Invoice Number">
              {selectedPayout.invoice_number}
            </Descriptions.Item>
            <Descriptions.Item label="Vendor Name">
              {selectedPayout.vendor_name}
            </Descriptions.Item>
            <Descriptions.Item label="Service Name">
              {selectedPayout.service_name}
            </Descriptions.Item>
            <Descriptions.Item label="Order ID">
              {selectedPayout.order_id}
            </Descriptions.Item>
            <Descriptions.Item label="Service Date">
              {selectedPayout.date_of_service ? new Date(selectedPayout.date_of_service).toLocaleDateString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <strong>${selectedPayout.amount}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Cycle">
              <Tag color={paymentCycleColors[selectedPayout.vendor_payment_cycle] || 'default'}>
                {selectedPayout.vendor_payment_cycle || 'N/A'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {selectedPayout.vendor_due_date ? new Date(selectedPayout.vendor_due_date).toLocaleDateString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColors[selectedPayout.status] || 'default'}>
                {selectedPayout.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Service Category">
              {selectedPayout.service_category}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {new Date(selectedPayout.created_at).toLocaleString()}
            </Descriptions.Item>
            {selectedPayout.uploaded_file_url && (
              <Descriptions.Item label="Document">
                <a 
                  href={selectedPayout.uploaded_file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ wordBreak: 'break-all' }}
                >
                  View Document
                </a>
              </Descriptions.Item>
            )}
            {/* Optional: Show reason if stored */}
            {selectedPayout.reason && (
              <Descriptions.Item label="Reason">
                {selectedPayout.reason}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Reason Input Modal (for Under Review / Rejected) */}
      <Modal
        title="Provide Reason for Status Change"
        open={reasonModalVisible}
        onCancel={() => {
          setReasonModalVisible(false);
          setPendingStatusChange(null);
          reasonForm.resetFields();
        }}
        onOk={() => reasonForm.submit()}
        okText="Continue"
        cancelText="Cancel"
      >
        <Form
          form={reasonForm}
          layout="vertical"
          onFinish={(values) => {
            if (!pendingStatusChange) return;
            const { payout, status } = pendingStatusChange;
            setPayoutToUpdate(payout);
            setNewStatus(status);
            setReasonModalVisible(false);
            setStatusChangeModalVisible(true);
          }}
        >
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason for this status change' }]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Explain why you are marking this payout as Under Review or Rejected..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Status Change Confirmation Modal */}
      <Modal
        open={statusChangeModalVisible}
        onCancel={() => {
          setStatusChangeModalVisible(false);
          setPayoutToUpdate(null);
          setNewStatus('');
          reasonForm.resetFields();
        }}
        onOk={handleStatusChange}
        okText="Confirm"
        cancelText="Cancel"
        title="Confirm Status Change"
      >
        {payoutToUpdate && (
          <div>
            <p>Are you sure you want to change the status of this payout?</p>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Invoice Number">
                {payoutToUpdate.invoice_number}
              </Descriptions.Item>
              <Descriptions.Item label="Vendor Name">
                {payoutToUpdate.vendor_name}
              </Descriptions.Item>
              <Descriptions.Item label="Current Status">
                <Tag color={statusColors[payoutToUpdate.status] || 'default'}>
                  {payoutToUpdate.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="New Status">
                <Tag color={statusColors[newStatus] || 'default'}>
                  {newStatus}
                </Tag>
              </Descriptions.Item>
              {reasonForm.getFieldValue('reason') && (
                <Descriptions.Item label="Reason">
                  {reasonForm.getFieldValue('reason')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VendorPayouts;